/**
 * Super API heartbeat (Web de Mi Boleta → api-taquilla).
 *
 * Autentica este frontend como un cliente OAuth2 (client-credentials) contra
 * api-taquilla y llama un endpoint público en bucle, de modo que aparezca como
 * "conectado + haciendo peticiones" en la zona de Super API de los admins.
 *
 * Flujo (RFC 6749 §4.4, cliente PÚBLICO):
 *   1. POST {API_TAQUILLA_URL}/v1/oauth/token  (x-www-form-urlencoded)
 *        grant_type=client_credentials&client_id=…   (SIN client_secret)
 *      → { access_token, token_type, expires_in, scope }  (NO enveloped)
 *   2. GET {API_TAQUILLA_URL}/v1/public/events con Authorization: Bearer <token>
 *   3. Cachea el token y lo refresca ~60s antes de expirar.
 *   4. Corre al arranque + cada 60s. Fire-and-forget, nunca rompe la app.
 *
 * SEGURIDAD: es un cliente OAuth2 PÚBLICO (first-party). El backend emite el
 * token SIN client_secret (no hay secreto en el bundle). La identidad solo
 * etiqueta la app para la auditoría/rate-limit; la autorización real es el JWT
 * de usuario que viaja en cada petición.
 */

import { API_TAQUILLA_URL } from './api/config';

const TOKEN_ENDPOINT = `${API_TAQUILLA_URL}/v1/oauth/token`;
const HEARTBEAT_ENDPOINT = `${API_TAQUILLA_URL}/v1/public/events`;

/** Cada cuánto repetir el ciclo (ms). */
const INTERVAL_MS = 60_000;
/** Margen para refrescar el token antes de su expiración real (s). */
const TOKEN_REFRESH_SKEW_SECONDS = 60;

interface OAuthTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope?: string;
}

interface CachedToken {
  accessToken: string;
  /** Epoch ms en el que consideramos el token expirado (con skew aplicado). */
  expiresAtMs: number;
}

let cachedToken: CachedToken | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;

function readCredentials(): { clientId: string } | null {
  const clientId = import.meta.env.VITE_MB_SUPERAPI_CLIENT_ID;
  if (typeof clientId !== 'string' || clientId.trim().length === 0) {
    return null;
  }
  return { clientId: clientId.trim() };
}

function isTokenValid(token: CachedToken | null): token is CachedToken {
  return token !== null && Date.now() < token.expiresAtMs;
}

// Cliente OAuth2 PÚBLICO (first-party): el backend emite el token SIN secret
// (no hay secreto en el bundle). La identidad solo etiqueta la app.
async function fetchToken(clientId: string): Promise<CachedToken> {
  const body = new URLSearchParams({
    grant_type: 'client_credentials',
    client_id: clientId,
  });

  const response = await fetch(TOKEN_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error(`token request failed: ${response.status}`);
  }

  const data = (await response.json()) as OAuthTokenResponse;
  if (typeof data.access_token !== 'string' || data.access_token.length === 0) {
    throw new Error('token response missing access_token');
  }

  const expiresInSeconds =
    typeof data.expires_in === 'number' && data.expires_in > 0
      ? data.expires_in
      : TOKEN_REFRESH_SKEW_SECONDS;
  const ttlMs = Math.max(expiresInSeconds - TOKEN_REFRESH_SKEW_SECONDS, 0) * 1000;

  return { accessToken: data.access_token, expiresAtMs: Date.now() + ttlMs };
}

async function getToken(clientId: string): Promise<string> {
  if (isTokenValid(cachedToken)) {
    return cachedToken.accessToken;
  }
  const fresh = await fetchToken(clientId);
  cachedToken = fresh;
  return fresh.accessToken;
}

/**
 * Token de cliente cacheado y válido, o null. Lo usa la capa http para adjuntar
 * `X-FC-Client-Token` en CADA petición → toda la app queda auditada por cliente
 * en la zona de peticiones. Síncrono: el heartbeat mantiene el token caliente.
 */
export function getCachedClientToken(): string | null {
  return isTokenValid(cachedToken) ? cachedToken.accessToken : null;
}

async function runOnce(clientId: string): Promise<void> {
  try {
    const accessToken = await getToken(clientId);
    const response = await fetch(HEARTBEAT_ENDPOINT, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.status === 401 || response.status === 403) {
      // Cliente desactivado o token inválido: descartamos el token cacheado y
      // reintentamos en el próximo ciclo. No rompemos la app.
      cachedToken = null;
      console.info('[superApiHeartbeat] desconectado (cliente desactivado o token inválido)');
      return;
    }

    if (!response.ok) {
      throw new Error(`heartbeat request failed: ${response.status}`);
    }
  } catch (error: unknown) {
    // Cualquier fallo de red/parsing: log silencioso, reintento próximo ciclo.
    cachedToken = null;
    const message = error instanceof Error ? error.message : 'unknown error';
    console.info(
      `[superApiHeartbeat] ciclo fallido, reintenta en ${INTERVAL_MS / 1000}s: ${message}`,
    );
  }
}

/**
 * Arranca el heartbeat: una corrida inmediata + intervalo cada 60s.
 * No-op silencioso si faltan credenciales o si ya está corriendo.
 */
export function startSuperApiHeartbeat(): void {
  if (intervalId !== null) {
    return;
  }
  const credentials = readCredentials();
  if (credentials === null) {
    return;
  }
  const { clientId } = credentials;

  void runOnce(clientId);
  intervalId = setInterval(() => {
    void runOnce(clientId);
  }, INTERVAL_MS);
}

/** Detiene el heartbeat (útil para cleanup en hot-reload / unmount). */
export function stopSuperApiHeartbeat(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
